import React, { FunctionComponent, useEffect, useState } from 'react';
import { ComplianceDataResponse } from './types/dashboard.types';
import MancoLevel from './components/MancoLevel';
import ProductFocus from './components/ProductFocus';
import MarketsSnapshot from './components/MarketsSnapshot';
import { useCustomDateRangeQuery } from './queries';
import GridItem from '../../layout/GridComponents/GridItem';
import dayjs from 'dayjs';
import { QueryBoundary } from '../../utilityComponents/QueryBoundary';
import { useSelector } from 'react-redux';
import { raptorPlatformIdSelector } from '../../../redux/raptorPlatform/selectors';
import { GeneralOverviewPageRenderedComponentProps } from '../../layout/general/SimpleGeneralPageWrapper';
import {
  EntityLabel,
  getEntityLabelForSection,
} from '../../../utilities/entityLabel';

/** Dashboard; ManCo vs Group wording comes from the section key. */
export const DashboardPage: React.FC<
  GeneralOverviewPageRenderedComponentProps
> = ({ section }) => {
  const entityLabel = getEntityLabelForSection(section);
  const raptorPlatformId = useSelector(raptorPlatformIdSelector);
  const today = dayjs().format('YYYY-MM-DD');
  const [activeRange, setActiveRange] = useState<{
    startDate: string;
    endDate: string;
  }>({
    startDate: today,
    endDate: today,
  });

  const isCurrentRange = activeRange.startDate === activeRange.endDate;

  const { data, isLoading, error } = useCustomDateRangeQuery(
    isCurrentRange ? null : activeRange.startDate,
    isCurrentRange ? null : activeRange.endDate,
    raptorPlatformId,
  );

  // Background fetches for Current and 7/30/90 day ranges to warm cache (disabled: only fetch active range)
  useCustomDateRangeQuery(
    dayjs().subtract(6, 'day').format('YYYY-MM-DD'),
    today,
    raptorPlatformId,
  );
  useCustomDateRangeQuery(
    dayjs().subtract(29, 'day').format('YYYY-MM-DD'),
    today,
    raptorPlatformId,
  );
  useCustomDateRangeQuery(
    dayjs().subtract(89, 'day').format('YYYY-MM-DD'),
    today,
    raptorPlatformId,
  );

  return (
    <QueryBoundary
      isLoading={isLoading}
      isError={!!error}
      data={data}
      loadingMessages={[
        'Loading dashboard...',
        'Analyzing compliance data...',
        'Preparing fund metrics...',
      ]}
      errorMessage={
        error
          ? (error as Error).message ||
          'An unexpected error occurred while fetching dashboard data.'
          : 'An unexpected error occurred while fetching dashboard data.'
      }
      noDataMessage="There is no data available for the selected date range."
    >
      {(dashboardData) => (
        <Dashboard
          data={dashboardData}
          activeRange={activeRange}
          setActiveRange={setActiveRange}
          regulationBreakdown={dashboardData.regulation_breakdown}
          entityLabel={entityLabel}
        />
      )}
    </QueryBoundary>
  );
};

export default DashboardPage;

interface DashboardProps {
  data: ComplianceDataResponse;
  activeRange: { startDate: string; endDate: string };
  setActiveRange: (r: { startDate: string; endDate: string }) => void;
  regulationBreakdown: any; // Will be properly typed as RegulationBreakdownData
  entityLabel: EntityLabel;
}

const Dashboard: FunctionComponent<DashboardProps> = ({
  data,
  activeRange,
  setActiveRange,
  regulationBreakdown,
  entityLabel,
}) => {
  const [dataInDateRange, setDataInDateRange] = useState(data);

  useEffect(() => {
    setDataInDateRange(data);
  }, [data]);

  return (
    <>
      <GridItem xs={12}>
        <div className="relative">
          <MancoLevel
            totalsForManco={{
              totalFails: {
                Fail: dataInDateRange.manco_data.status_count.Fail,
                Alert: dataInDateRange.manco_data.status_count.Alert,
                Pass: dataInDateRange.manco_data.status_count.Pass,
              },
              totalFunds: dataInDateRange.manco_data.total_fund_count,
              totalReportedFunds: dataInDateRange.manco_data.fund_count,
              totalAUM: dataInDateRange.manco_data.aum,
            }}
            onToggleChange={setActiveRange}
            dataInDateRange={dataInDateRange}
            activeCustomRange={activeRange}
            regulationBreakdown={regulationBreakdown}
            entityLabel={entityLabel}
          />
        </div>
      </GridItem>

      <GridItem xs={12} lg={6} style={{ display: 'flex' }}>
        <ProductFocus />
      </GridItem>
      <GridItem xs={12} lg={6} style={{ display: 'flex' }}>
        <MarketsSnapshot />
      </GridItem>
    </>
  );
};
